import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, TrendingUp, X } from "lucide-react";
import { toast } from "sonner";
import { getAnalytics } from "@/lib/api";
import { categoryColor, formatEUR, formatEUR0, formatSigned, monthNameIT } from "@/lib/format";

const PACE_META = {
  ok: { color: "#00FF87", label: "In linea" },
  warning: { color: "#F5A623", label: "Attenzione" },
  over: { color: "#FF4D4D", label: "Oltre budget" },
};

const RANGES = [
  { days: 7, label: "7G" },
  { days: 30, label: "30G" },
  { days: 90, label: "90G" },
];

/* ---------- grafico cashflow (area + linea) in SVG ---------- */
const CashflowChart = ({ data }) => {
  const W = 320;
  const H = 120;
  const pad = 10;

  const geom = useMemo(() => {
    if (!data || data.length < 2) return null;
    const vals = data.map((d) => d.balance);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min || 1;
    const stepX = (W - pad * 2) / (data.length - 1);
    const x = (i) => pad + i * stepX;
    const y = (v) => pad + (H - pad * 2) * (1 - (v - min) / range);
    const line = data.map((d, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(d.balance).toFixed(1)}`).join(" ");
    const area = `${line} L${x(data.length - 1).toFixed(1)} ${H - pad} L${x(0).toFixed(1)} ${H - pad} Z`;
    return { line, area, min, max, lastX: x(data.length - 1), lastY: y(vals[vals.length - 1]) };
  }, [data]);

  if (!geom) {
    return <div className="flex h-32 items-center justify-center text-[12px] text-white/30">Dati insufficienti</div>;
  }

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-32 w-full" preserveAspectRatio="none" data-testid="cashflow-chart">
        <defs>
          <linearGradient id="cf-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00FF87" stopOpacity="0.30" />
            <stop offset="100%" stopColor="#00FF87" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={geom.area} fill="url(#cf-grad)" />
        <path
          d={geom.line}
          fill="none"
          stroke="#00FF87"
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        <circle cx={geom.lastX} cy={geom.lastY} r="2.6" fill="#00FF87" />
      </svg>
      <div className="mono pointer-events-none absolute right-2 top-1 text-[10px] tracking-[0.08em] text-white/40">
        max {formatEUR0(geom.max)}
      </div>
      <div className="mono pointer-events-none absolute bottom-1 right-2 text-[10px] tracking-[0.08em] text-white/40">
        min {formatEUR0(geom.min)}
      </div>
    </div>
  );
};

const Stat = ({ label, value, color, testid }) => (
  <div className="flex-1 rounded-[8px] border border-white/[0.06] bg-[#0D0D14] px-3 py-2.5" data-testid={testid}>
    <p className="caps-tag text-white/35">{label}</p>
    <p className="mono mt-1 text-[15px] font-medium tabular-nums" style={{ color }}>
      {value}
    </p>
  </div>
);

export const AnalyticsSheet = ({ open, onClose }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  const load = useCallback(async (d) => {
    setData(null);
    try {
      setData(await getAnalytics(d));
    } catch {
      toast.error("Impossibile caricare i grafici");
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      load(days);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, days, load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const maxCat = data?.by_category?.[0]?.amount || 1;

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50" data-testid="analytics-sheet">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-x-0 bottom-0 top-12 mx-auto flex max-w-[430px] flex-col overflow-hidden rounded-t-[12px] border border-b-0 border-white/[0.08] bg-[#030305]"
          >
            <div className="flex items-center justify-between border-b border-white/[0.06] px-4 pb-3 pt-3">
              <span className="mono flex items-center gap-2 text-sm font-medium tracking-[0.1em] text-[#00FF87]">
                <TrendingUp size={15} />
                ANALYTICS <span className="text-white/25">//</span> LIQO
              </span>
              <button
                onClick={onClose}
                data-testid="analytics-close-button"
                aria-label="Chiudi"
                className="flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05]"
              >
                <X size={14} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {data === null ? (
                <div className="space-y-4" data-testid="analytics-loading">
                  <div className="h-16 animate-pulse rounded-[8px] border border-white/[0.06] bg-[#0D0D14]" />
                  <div className="h-32 animate-pulse rounded-[8px] border border-white/[0.06] bg-[#0D0D14]" />
                  <div className="h-40 animate-pulse rounded-[8px] border border-white/[0.06] bg-[#0D0D14]" />
                </div>
              ) : (
                <>
                  {/* stat mese */}
                  <div className="flex gap-2">
                    <Stat label="Entrate mese" value={formatEUR0(data.month_income)} color="#00FF87" testid="stat-income" />
                    <Stat label="Uscite mese" value={formatEUR0(data.month_spent)} color="#FF7A9C" testid="stat-spent" />
                    <Stat
                      label="Netto"
                      value={formatSigned(data.month_net)}
                      color={data.month_net >= 0 ? "#00FF87" : "#FF4D4D"}
                      testid="stat-net"
                    />
                  </div>

                  {/* ritmo di spesa del mese */}
                  {data.pace && data.pace.status !== "none" && (() => {
                    const p = data.pace;
                    const meta = PACE_META[p.status] || PACE_META.ok;
                    return (
                      <div className="mt-4 rounded-[10px] border border-white/[0.06] bg-[#0D0D14] p-3.5" data-testid="spending-pace">
                        <div className="flex items-center justify-between">
                          <p className="caps-tag text-white/50">Ritmo di spesa · {monthNameIT()}</p>
                          <span className="mono text-[11px]" style={{ color: meta.color }} data-testid="pace-status">{meta.label}</span>
                        </div>
                        <div className="mono mt-2 flex items-baseline gap-1.5">
                          <span className="text-[20px] font-light tabular-nums text-white/90" data-testid="pace-spent">{formatEUR0(p.spent)}</span>
                          <span className="text-[12px] text-white/40">/ {formatEUR0(p.budget)} budget</span>
                        </div>
                        <div className="relative mt-2 h-2 overflow-hidden rounded-full bg-white/[0.05]">
                          <div className="h-full rounded-full transition-[width] duration-300" style={{ width: `${Math.min(p.pct_spent, 100)}%`, backgroundColor: meta.color }} />
                          {p.pct_projected > 0 && p.pct_projected <= 130 && (
                            <div className="absolute top-[-2px] h-3 w-[2px] bg-white/70" style={{ left: `calc(${Math.min(p.pct_projected, 100)}% - 1px)` }} title="Proiezione fine mese" />
                          )}
                        </div>
                        <div className="mono mt-1.5 flex items-center justify-between text-[10px] tracking-[0.03em] text-white/35">
                          <span>Giorno {p.days_elapsed}/{p.days_in_month}</span>
                          <span data-testid="pace-projected">
                            Proiezione <span style={{ color: meta.color }}>{formatEUR0(p.projected_spend)} ({p.pct_projected}%)</span>
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* cashflow */}
                  <div className="mt-5">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="caps-tag text-white/50">Cashflow · saldo</p>
                      <div className="flex gap-1" data-testid="analytics-range-toggle">
                        {RANGES.map((r) => (
                          <button
                            key={r.days}
                            onClick={() => setDays(r.days)}
                            data-testid={`analytics-range-${r.days}`}
                            className={`mono rounded-[5px] border px-2 py-1 text-[10px] tracking-[0.1em] transition-colors ${
                              days === r.days
                                ? "border-[#00FF87]/40 bg-[#00FF87]/[0.08] text-[#00FF87]"
                                : "border-white/[0.08] text-white/45 hover:bg-white/[0.04]"
                            }`}
                          >
                            {r.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-[10px] border border-white/[0.06] bg-[#0D0D14] p-2 pt-3">
                      <CashflowChart data={data.cashflow} />
                    </div>
                  </div>

                  {/* spese per categoria */}
                  <div className="mt-5">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="caps-tag text-white/50">Spese per categoria · mese</p>
                      <span className="mono text-[11px] text-white/40">{formatEUR(data.total_expense_month)}</span>
                    </div>

                    {data.by_category.length === 0 ? (
                      <p className="mono py-6 text-center text-[12px] text-white/30">Nessuna spesa questo mese</p>
                    ) : (
                      <div className="space-y-2.5" data-testid="category-breakdown">
                        {data.by_category.map((c) => {
                          const color = categoryColor(c.category);
                          return (
                            <div key={c.category} data-testid={`category-row-${c.category}`}>
                              <div className="mb-1 flex items-center justify-between">
                                <span className="flex items-center gap-2 text-[12px] font-light text-white/75">
                                  <span className="h-2 w-2 rounded-[2px]" style={{ backgroundColor: color }} />
                                  {c.category}
                                </span>
                                <span className="mono text-[11px] tabular-nums text-white/55">
                                  {formatEUR(c.amount)} · {c.pct}%
                                </span>
                              </div>
                              <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.05]">
                                <div
                                  className="h-full rounded-full transition-[width] duration-300"
                                  style={{ width: `${Math.max((c.amount / maxCat) * 100, 3)}%`, backgroundColor: color }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="mt-5 flex items-center justify-center gap-4 text-[10px] text-white/25">
                    <span className="flex items-center gap-1">
                      <ArrowUpRight size={11} className="text-[#00FF87]" /> entrate
                    </span>
                    <span className="flex items-center gap-1">
                      <ArrowDownRight size={11} className="text-[#FF7A9C]" /> uscite
                    </span>
                    <span>· {data.range_days} giorni</span>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
